import NumberInput from '../../components/NumberInput.tsx';
import { currencyFormat } from '../../utils/currencyUtils.ts';
import { PaymentType } from '../../enums/PaymentType.ts';
import { UpsellAPINS } from '../../api/upsell.api.ts';
import { MenuSelect } from './Upsell.tsx';
import styled, { css } from 'styled-components';
import { useTranslation } from 'react-i18next';
import { Currency } from '@dinero.js/currencies';
import { CheckCircled } from '../../icons/CheckCircled.tsx';

const Card = styled.li<{ isAvailable: boolean }>`
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    background: #ffffff;
    border: 1px solid #e9eef3;
    border-radius: 14px;
    padding: 22px 24px;
    transition: box-shadow 0.15s ease, border-color 0.15s ease,
        transform 0.12s ease;

    &:hover {
        border-color: #dfe5ec;
        box-shadow: 0 12px 26px -18px rgba(20, 15, 8, 0.4);
    }

    ${({ isAvailable }) =>
        !isAvailable &&
        css`
            opacity: 0.55;
            pointer-events: none;
        `};

    .menu-main-content {
        display: flex;
        width: 100%;
        justify-content: space-between;
        flex-grow: 1;
        gap: 16px;
        align-items: flex-start;

        .pre-selected {
            display: flex;
            align-items: center;
            color: #ff6e3d;
            font-size: 13px;
            font-weight: 600;
            white-space: nowrap;

            div:nth-child(2) {
                margin-left: 4px;
            }
        }

        > .menu-item-body {
            display: flex;
            align-items: flex-start;
            flex-direction: column;
            justify-content: center;

            > div {
                > div:first-child {
                    /* Menu name */
                    font-weight: 600;
                    font-size: 16px;
                    line-height: 1.3;
                    color: #1c1c1c;
                    overflow: hidden;
                }
            }

            /* Short description */

            p {
                margin: 6px 0px 0px 0px;
                line-height: 1.5;
                font-size: 13.5px;
                color: #6f6a63;
            }
        }

        > .menu-nrinp-wrapper {
            /* Number input wrapper */
            display: flex;
            align-items: center;
            flex-shrink: 0;

            div:first-child {
                margin-right: 16px;
            }
        }
    }

    .menu-detail-wrapper {
        display: flex;
        flex-wrap: wrap;
        width: 100%;
        margin-top: 16px;
        padding-top: 14px;
        align-items: center;

        font-size: 13px;
        color: #9b9b9b;
        border-top: 1px solid #f0f0f0;

        b {
            color: #1c1c1c;
            font-weight: 600;
        }

        > *:not(:last-child) {
            position: relative;
            margin: 0px 20px 0px 0px;

            &::after {
                content: '';
                position: absolute;
                top: 50%;
                left: calc(100% + 8px);
                transform: translateY(-50%);

                width: 4px;
                height: 4px;
                border-radius: 50%;
                background-color: #d4d4d4;
            }
        }
    }
`;

export const MenuCard = ({
    menu: m,
    selectedMenus,
    guests: guestsInt,
    currentFPId,
    floorPlanId,
    totalSelectedGuests,
    isFloorPlanSelected,
    prevFloorPlanId,
    updateSelected,
    currency,
    locale,
    preselectedMenuId,
}: {
    menu: UpsellAPINS.MenuWithIsAvailable;
    selectedMenus: MenuSelect[];
    guests: number;
    currentFPId?: number;
    floorPlanId: number;
    updateSelected: (
        direction: 'Increment' | 'Decrement',
        menuId: number,
    ) => void;
    totalSelectedGuests: number;
    isFloorPlanSelected: (menu: any, floorPlanId: string | number) => boolean;
    prevFloorPlanId?: number;
    currency: Currency<number>;
    locale: string;
    preselectedMenuId: number | null;
}) => {
    const { t } = useTranslation();

    const uiMinGuests =
        m.isExclusive && m.minGuests <= guestsInt ? guestsInt : m.minGuests;

    if (!m.isAvailable) {
        return null;
    }

    return (
        <Card key={m.id} isAvailable={m.isAvailable}>
            <div className="menu-main-content">
                <div className="menu-item-body">
                    <div>
                        <div>{m.name}</div>
                        <p>{m.shortDescription}</p>
                    </div>
                </div>

                <div className="menu-nrinp-wrapper">
                    <>
                        {preselectedMenuId === m.id && (
                            <div className={'pre-selected'}>
                                <CheckCircled size={16} color={'#FF6E3D'} />
                                <div>{t('pages.upsell.preselected')}</div>
                            </div>
                        )}
                        <NumberInput
                            value={
                                selectedMenus.find((x) => x.id === m.id)
                                    ?.count ?? 0
                            }
                            disabled={m.minGuests > guestsInt || !m.isAvailable}
                            incrementDisabled={
                                currentFPId === floorPlanId &&
                                (totalSelectedGuests === guestsInt ||
                                    m.minGuests > guestsInt ||
                                    totalSelectedGuests + m.minGuests >
                                        guestsInt)
                            }
                            steppersFaded={
                                selectedMenus.length !== 0 &&
                                !isFloorPlanSelected(m, currentFPId || -1)
                            }
                            doFlashReset={
                                typeof currentFPId === 'number' &&
                                typeof prevFloorPlanId === 'number' &&
                                currentFPId !== prevFloorPlanId &&
                                !m.floorPlanIds.includes(floorPlanId as number)
                            }
                            className="item"
                            onChange={(val) => updateSelected(val.type, m.id)}
                        />
                    </>
                </div>
            </div>
            <div className="menu-detail-wrapper">
                <div>
                    <b>
                        {m.paymentAmount && m.paymentAmount > 0
                            ? `${currencyFormat(
                                  m.paymentAmount,
                                  currency,
                                  locale,
                              )} `
                            : t('pages.upsell.priceVaries')}
                        &nbsp;
                    </b>
                    {m.paymentType === PaymentType.PrePayment && (
                        <span>
                            {t('pages.upsell.prepayment').toLowerCase()}
                        </span>
                    )}
                    {m.paymentType === PaymentType.NoShow && (
                        <span>{t('pages.upsell.noShowFee').toLowerCase()}</span>
                    )}
                    {m.paymentType !== PaymentType.None &&
                        !!m.paymentAmount && (
                            <span>
                                {' '}
                                {m.pricePerBooking
                                    ? t('pages.upsell.perBooking')
                                    : t('pages.upsell.perGuest')}
                            </span>
                        )}
                </div>
                {uiMinGuests > 0 && (
                    <div>{`${uiMinGuests} ${t('pages.upsell.minGuests')}`}</div>
                )}
                {(m.maxGuests ?? 0) > 0 && (
                    <div>{`${m.maxGuests} ${t('pages.upsell.maxGuests')}`}</div>
                )}
            </div>
        </Card>
    );
};
